import { useState } from "react";
import styles from "./EAFSGAreas.module.scss";
import { Button, Modal, Table, TablePaginationConfig, Tooltip, Typography } from "antd";
import Search from 'antd/lib/input/Search';
import { useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PlusOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { theme } from "../../theme";
import Layout from "../../Containers/Layout";
import { NOTIFICATION_TYPES, openNotification } from "../Notifications/NotificationsUtils";
import { deleteEAFSGArea, getEAFSGAreas } from "../../Requests/eafsg-area-requests";

const EAFSGAreas = () => {
    const { t } = useTranslation();
    const [searchTermName, setSearchTermName] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrent] = useState(1);

    const [modalPropsDelete, setModalPropsDelete] = useState({
        isModalVisible: false,
        title: '',
        id: '',
    });

    const handleShowDelete = (id: string) => {
        setModalPropsDelete(modalProps => ({ ...modalProps, isModalVisible: true, id }));
    }

    const handleCloseDelete = () => {
        setModalPropsDelete(modalProps => ({ ...modalProps, isModalVisible: false }));
    }

    const handleOkDelete = () => {
        setModalPropsDelete(modalProps => ({ ...modalProps, isModalVisible: false }));
        deleteEAFSGArea(modalPropsDelete.id)
            .then(() => {
                deleteSucceded();
                invalidateQuery();
            })
            .catch(deleteFailed);
    }

    const handleTableChange = (pagination: TablePaginationConfig) => {
        setCurrent(() => pagination.current ?? 1);
        setPageSize(() => pagination.pageSize ?? 10);
    };

    const deleteSucceded = () => {
        openNotification(
            'Succes',
            'Ștergerea s-a realizat cu succes!',
            NOTIFICATION_TYPES.SUCCESS
        );
    };

    const deleteFailed = async (err: any) => {
        let details = '';
        try {
            if (err?.text) details = await err.text();
            else if (err?.response?.text) details = await err.response.text();
            else if (typeof err?.message === 'string') details = err.message;
        } catch {
            // ignore parse errors
        }

        openNotification(
            'Eroare',
            `Ștergerea nu s-a realizat cu succes!${details ? ' ' + details : ''}`,
            NOTIFICATION_TYPES.ERROR
        );
    };

    const queryClient = useQueryClient();

    const invalidateQuery = () => { queryClient.invalidateQueries('getEAFSGAreas') };

    const openFetchError = (_error: any) => {
        openNotification(
            'Eroare!',
            'Datele nu au putut fi aduse din server!',
            NOTIFICATION_TYPES.ERROR
        );
    }

    const { data, isLoading } = useQuery(['getEAFSGAreas', searchTermName, pageSize, currentPage],
        () => {
            return getEAFSGAreas(searchTermName, currentPage, pageSize);
        },
        {
            onError: openFetchError,
        }
    );

    const FilterByNameInput = (
        <Search placeholder={t('account.name')} allowClear onSearch={setSearchTermName} style={{ width: '100%' }} />
    );

    const columns = [
        {
            title: FilterByNameInput,
            dataIndex: 'name',
            render: (text: string, record: any) => record.name
        },
        {
            title: 'Descriere',
            dataIndex: 'description',
            render: (text: string, record: any) => <div style={{ whiteSpace: 'pre-wrap' }}>{record.description}</div>
        },
        {
            title: 'Acțiuni',
            width: '100px',
            render: (text: string, record: any) =>
                <div>
                    <Link to={"/editare-arie-eafsg/" + record.id}><Tooltip title='Editare'>
                        <FontAwesomeIcon icon={solid('edit')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px" }} />
                    </Tooltip></Link>
                    <Tooltip title='Ștergere'>
                        <FontAwesomeIcon icon={solid('trash')} style={{ marginLeft: '20px', cursor: 'pointer', height: "1rem", padding: "6px 0px" }}
                            onClick={() => handleShowDelete(record.id)} />
                    </Tooltip>
                </div>
        }
    ];

    return <Layout>
      <div className={styles.container} style={{ marginTop: "0%" }}>
        <Modal
            open={modalPropsDelete.isModalVisible}
            onOk={handleOkDelete}
            onCancel={handleCloseDelete}
            title="Ștergere"
            width={"400px"}
            okText={t('account.confirm')}
            cancelText={t('account.cancel')}
            cancelButtonProps={{ style: { background: theme.green, border: theme.green, color: theme.white, borderRadius: "10px" } }}
            okButtonProps={{ style: { background: theme.secondColor, border: theme.secondColor, borderRadius: "10px" } }}
        >
            <div>
                <Typography>Sigur doriți ștergerea ariei EAFSG?</Typography>
            </div>
        </Modal>

        <Table
            dataSource={data?.data || []}
            scroll={{ y: 'calc(100vh - 330px)' }}
            sticky={true}
            pagination={{
                total: data?.totalCount,
                current: currentPage,
                pageSize: data?.pageSize,
                pageSizeOptions: ["10", "20", "50"],
                defaultPageSize: 10,
                showSizeChanger: true,
                showTotal: (_total: number) => {
                    return <div>
                        <Tooltip title="Adaugă arie EAFSG">
                            <Link to='/adaugare-arie-eafsg'>
                                <Button type="primary" icon={<PlusOutlined />}>
                                    Adaugă arie EAFSG
                                </Button>
                            </Link>
                        </Tooltip>
                    </div>
                },
                hideOnSinglePage: false,
                locale: { items_per_page: t('configuration.pagination') },
                position: ['topRight']
            }}
            locale={{
                emptyText: (
                    <span>
                        <h3>Nu există arii EAFSG adăugate! Apasă butonul de mai jos pentru a adăuga o arie nouă!</h3>
                        <Tooltip title="Adaugă arie EAFSG">
                            <Link to='/adaugare-arie-eafsg'>
                                <Button type="primary" icon={<PlusOutlined />}>
                                    Adaugă arie EAFSG
                                </Button>
                            </Link>
                        </Tooltip>
                    </span>
                )
            }}
            columns={columns}
            rowKey={record => record.id!}
            loading={isLoading}
            onChange={handleTableChange}
        />
      </div>
    </Layout>
}

export default EAFSGAreas;
